// askConversation — in-memory follow-up context for Ask BigAgri (P1.4).
//
// Both surfaces (⌘K Ask view, /ask floor mode) share one short conversation
// buffer so "and last week?" works after a tipping question. Deliberately
// NOT persisted: it lives for the page session and entries expire after ten
// minutes, so this morning's context can't quietly warp tonight's question.
// The gateway independently caps history (6 turns / 1 000 chars each).
//
// Tenant-scoped: callers pass the active tenant id (they have hook access;
// this module does not) and a tenant switch clears the buffer — cross-tenant
// history must never enter the prompt. A null/undefined id means the caller's
// tenant hook hasn't resolved yet (fresh mount) — that is UNKNOWN, not "no
// tenant": scope and buffer stay put, or every remount would wipe the
// follow-up context mid-conversation (the null-flap bug).

const TTL_MS = 10 * 60_000
const MAX_TURNS = 6

interface Turn { role: 'user' | 'assistant'; content: string; at: number }

let turns: Turn[] = []
let scopeTenantId: string | null = null   // null = no REAL tenant seen yet

// Only a real id that DIFFERS from a previously-known real id clears; an
// unknown (null) caller keeps whatever scope is current, and the first real
// id adopts any turns pushed before the hook resolved (same page session,
// same active tenant — the gateway resolves the tenant from auth anyway).
function ensureScope(tenantId: string | null): void {
  if (tenantId == null) return
  if (scopeTenantId !== null && scopeTenantId !== tenantId) turns = []
  scopeTenantId = tenantId
}

export function askHistory(tenantId: string | null): Array<{ role: string; content: string }> {
  ensureScope(tenantId)
  const cutoff = Date.now() - TTL_MS
  turns = turns.filter(t => t.at >= cutoff)
  return turns.slice(-MAX_TURNS).map(({ role, content }) => ({ role, content }))
}

export function pushAskTurn(tenantId: string | null, role: 'user' | 'assistant', content: string): void {
  ensureScope(tenantId)
  const trimmed = content.trim().slice(0, 1000)
  if (!trimmed) return
  turns.push({ role, content: trimmed, at: Date.now() })
  if (turns.length > MAX_TURNS * 2) turns = turns.slice(-MAX_TURNS * 2)
}
